import type { Language } from '@/config/objects/languages';
import {
  Breadcrumbs,
  Button,
  ConfirmDialog,
  DetailPageHeader,
  DetailPageHero,
  PageContainer,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

const tabs = [
  { label: 'Overview', path: 'overview', icon: Globe },
];

export function LanguageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data, isLoading } = useQuery<{ success: boolean; data: Language }>({
    queryKey: ['Language', id],
    queryFn: () => apiClient.get(`/languages/${id}`),
    enabled: !!id,
  });

  const language = data?.data;

  const deleteMutation = useMutation({
    mutationFn: async (languageId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/languages/${languageId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['Language'] });
        navigate('/languages');
      } else {
        throw new Error(response.error || 'Failed to delete language');
      }
    },
  });

  const handleDelete = () => {
    if (!language) return;
    toast.promise(deleteMutation.mutateAsync(language.id), {
      loading: 'Deleting language...',
      success: 'Language deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete language',
    });
  };

  useEffect(() => {
    if (id) {
      const basePath = `/languages/${id}`;
      if (location.pathname === basePath || location.pathname === `${basePath}/`) {
        navigate(`${basePath}/overview`, { replace: true });
      }
    }
  }, [id, location.pathname, navigate]);

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Languages', href: '/languages' },
    { label: language?.name || 'Language', current: true },
  ];

  const basePath = `/languages/${id}`;

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-lg font-medium mb-2">Language Not Found</h2>
          <p className="text-muted-foreground">
            The requested language could not be found.
          </p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer padding="none" className="flex flex-col overflow-hidden">
      <DetailPageHeader
        actions={
          language && (
            <>
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          )
        }
      >
        <Breadcrumbs items={breadcrumbs} />
      </DetailPageHeader>

      <DetailPageHero
        isLoading={isLoading}
        category={{ label: 'Language', icon: Globe }}
        title={language?.name || 'Language'}
        image={{
          url: null,
          alt: language?.name || 'Language',
          fallbackIcon: Globe,
        }}
        meta={language?.code && (
              <span className="text-sm text-white/60 font-mono">{language.code}</span>
            )}
        tabs={tabs}
        basePath={basePath}
      >
        <Outlet context={{ language, isLoading }} />
      </DetailPageHero>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Language"
        description={`Are you sure you want to delete "${language?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </PageContainer>
  );
}
